<template>
  <div class="ma-5">
    <!-- Filtros de zones y fecha -->
    <div class="filter__container">
      <FilterAutocomplete
        v-model="filter.zones"
        :items="zones"
        label="Filtrar por Desarrollos"
      />
      <dateRange
        v-model="filter.date"
        default="Últimos 30 días"
        ref="dateRange"
        dense
      />
      <div style="display: flex; gap: 0.5rem;">
        <v-btn color="primary white--text" @click="fetchDataByReport">
          Filtrar
        </v-btn>
        <v-btn
          color="grey lighten-2 grey--text text--darken-4"
          @click="cleanFilters"
        >
          Limpiar Filtros
        </v-btn>
      </div>
      <!-- Filtros de descartado, posibilidad e inmediatez -->
      <div style="display: flex; flex-direction: column; ">
        <SimpleSwitch
          v-model="showDiscarded"
          label="Descartados"
          hint="Muestra el numero de leads descartados"
        />
        <SimpleSwitch v-model="showBudgetPhase" label="Posibilidad por fase" />
        <SimpleSwitch v-model="showPercent" label="Porcentaje" />
        <SimpleSwitch
          v-model="showBrex"
          label="Brokers Externos"
          hint="Mostrar brokers externos registrados por el asesor."
        />
        <SimpleSwitch
          v-model="showZoom"
          label="Zoom agendado"
          hint="Muestra leads con Zoom agendado (Asignado, Por Perfilar, Seguimiento)"
        />
        <SimpleSwitch
          v-model="showVirtualMeeting"
          label="Reunión virtual"
          hint="Muestra leads con Reunión virtual (Asignado, Por Perfilar, Seguimiento)"
        />
      </div>
    </div>
    <!-- Tabla -->
    <ViewLoading v-if="loading" />
    <v-data-table
      v-else
      :headers="headers"
      :items="items"
      :items-per-page="-1"
      class="elevation-1"
    >
      <!-- groupHeader -->
      <template v-slot:[`header`]>
        <Subheading
          :subheading="subheading"
          :total="countAll[type]"
          :totalPercent="getPercent(countAll[type], countAll.total)"
          :initColspan="initColspan"
          :totalColspan="totalColspan"
          :showDiscarded="showDiscarded"
        />
      </template>
      <!-- Headers -->
      <template #header.totalByContact>
        <span> Leads </span>
        <HelperMessage>
          <v-chip color="white">
            {{ countAll.total }}
          </v-chip>
          <template #message> Total: {{ countAll.total }} </template>
        </HelperMessage>
      </template>
      <template #header.brexByContact>
        <HelperMessage>
          <div style="display: flex; gap: 0.25rem;">
            Brokers externos
            <v-icon small color="white">mdi-help-circle</v-icon>
          </div>
          <template #message>
            <p class="pa-0 ma-0">
              Brokers externos registrados por el asesor.
            </p>
          </template>
        </HelperMessage>
      </template>
      <template v-slot:[`header.inmediatly`]>
        <HelperMessage>
          <v-icon class="pa-1" size="22" color="amber">
            mdi-lightning-bolt
          </v-icon>
          <template #message>
            Lead contactado en menos de 5 minutos.
          </template>
        </HelperMessage>
      </template>
      <template v-slot:[`header.probability`]>
        <HelperMessage>
          <img class="logoThermometer" :src="thermometer" />
          <template #message>
            Probables
          </template>
        </HelperMessage>
      </template>
      <template v-slot:[`header.finished`]>
        <div>
          Venta
          <v-chip class="v-chipActive" color="white">
            {{ getValueAllLeadsByPhase("finished", "active") }}
          </v-chip>
          <span class="percentStyle">
            {{
              getPercent(
                getValueAllLeadsByPhase("finished", "active"),
                countAll.total
              )
            }}%
          </span>
        </div>
      </template>
      <template v-slot:[`header.discarded`]>
        <div>
          Descartado
          <v-chip class="v-chipActive" color="white">
            {{ countAll.discarded }}
          </v-chip>
          <span class="percentStyle">
            {{ getPercent(countAll.discarded, countAll.total) }}%
          </span>
        </div>
      </template>
      <!-- use for by headers -->
      <template
        v-for="{ value, text } in customHeaders"
        v-slot:[`header.${value}`]
      >
        <div :key="value">
          <v-col class="pa-2" cols="12" sm="12">
            {{ text }}
            <v-chip class="v-chipActive" color="white">
              {{ getValueAllLeadsByPhase(value, type) }}
            </v-chip>
            <span class="percentStyle">
              {{
                getPercent(
                  getValueAllLeadsByPhase(value, type),
                  countAll.total
                )
              }}%
            </span>
          </v-col>
          <span v-if="showBudgetPhase" class="mx-auto budget-phase exchange">{{
            calculateAllBudgetByPhase(value)
          }}</span>
        </div>
      </template>
      <!-- Items -->
      <template v-slot:[`item.totalByContact`]="{ item }">
        <strong>
          {{ item.totalByContact }}
        </strong>
        <HelperMessage>
          <span
            style="cursor: pointer;"
            class="blue--text text--accent-4 percentStyle"
          >
            {{ countAllTypeByContac(item, "active") }}
          </span>
          <template #message>Activos</template>
        </HelperMessage>
        <span class="grey--text text--darken-1 percentStyle">
          ({{
            getPercent(
              countAllTypeByContac(item, "active"),
              item.totalByContact
            )
          }}%)
        </span>
      </template>
      <template #item.brexByContact="{ item:{ brexByContact} }">
        {{ brexByContact ?? "-" }}
      </template>
      <template v-slot:[`item.inmediatly`]="{ item }">
        <WrapperItemComertialReport>
          {{
            getPercent(
              countAllTypeByContac(item, "inmediatez"),
              item.totalByContact,
              0
            )
          }}%
        </WrapperItemComertialReport>
      </template>
      <template v-slot:[`item.probability`]="{ item }">
        <WrapperItemComertialReport>
          {{ countAllTypeByContac(item, "probability") }}
        </WrapperItemComertialReport>
      </template>
      <template v-slot:[`item.finished`]="{ item }">
        <WrapperItemComertialReport>
          <ItemDataReport
            :item="item"
            phase="finished"
            :total="getValuePhasByContact(item, 'finished', 'active')"
            :func="() => detecPhase(item._id, 'finished')"
            :showBudgetPhase="showBudgetPhase"
            :budget="calculatePhaseBudgetByContact(item, 'finished')"
          ></ItemDataReport>
        </WrapperItemComertialReport>
      </template>
      <template v-slot:[`item.discarded`]="{ item }">
        <WrapperItemComertialReport>
          <ItemDataReport
            :item="item"
            phase="discarded"
            :total="countAllTypeByContac(item, 'discarded')"
            :func="() => detecPhase(item._id, 'discarded')"
          />
        </WrapperItemComertialReport>
      </template>
      <!-- for by customHeadears in items -->
      <template
        v-for="{ value: head } in customHeaders"
        #[`item.${head}`]="{ item }"
      >
        <WrapperItemComertialReport :key="head">
          <ItemDataReport
            :item="item"
            :phase="head"
            :total="getValuePhasByContact(item, head, type)"
            :showBudgetPhase="showBudgetPhase"
            :budget="calculatePhaseBudgetByContact(item, head)"
            :func="() => detecPhase(item._id, head)"
            :showPercent="showPercent"
            :showZoom="showZoom && isIndicatorPhase(head)"
            :zoomCount="getValuePhasByContact(item, head, 'zoom')"
            :zoomFunc="() => detecPhase(item._id, head, { hasZoom: true })"
            :showVirtualMeeting="showVirtualMeeting && isIndicatorPhase(head)"
            :virtualMeetingCount="
              getValuePhasByContact(item, head, 'virtualMeeting')
            "
            :virtualMeetingFunc="
              () => detecPhase(item._id, head, { hasVirtualMeeting: true })
            "
          />
        </WrapperItemComertialReport>
      </template>
    </v-data-table>
    <ListLeads
      :leads="detail.leads"
      :dialog="detail.dialog"
      :loading="detail.loading"
      @setDialog="value => (detail.dialog = value)"
    />
  </div>
</template>

<script>
import {
  CURRENCY_FORMATER,
  PHASE_CLASS,
  PHASE_MAP,
  SUBHEADING,
  buildHeaders
} from "../../constants/report/comertial";
import { mapState, mapActions, mapGetters } from "vuex";
import dateRange from "@/components/reports-kpis/component/dateRange.component.vue";
import ItemDataReport from "./component/itemDataTableReport.vue";
import FilterAutocomplete from "./component/FilterAutocomplete.vue";
import WrapperItemComertialReport from "./component/wrapperItemComertialReport.vue";

export default {
  name: "ComertialReportV2",
  components: {
    dateRange,
    ItemDataReport,
    FilterAutocomplete,
    WrapperItemComertialReport,
    HelperMessage: () => import("../common/HelperMessage.vue"),
    ViewLoading: () => import("./component/comertial/ViewLoading.vue"),
    Subheading: () => import("./component/comertial/Subheading.vue"),
    SimpleSwitch: () => import("../common/SimpleSwitch.vue"),
    ListLeads: () =>
      import("@/components/lead/components/simpleListLeadsModal/index.vue")
  },
  data() {
    return {
      showBrex: false,
      showPercent: false,
      loading: true,
      showBudgetPhase: false,
      showDiscarded: false,
      showZoom: false,
      showVirtualMeeting: false,
      type: "active", // active, discarded
      thermometer: `${process.env.BASE_URL}50rojo.png`,
      detail: {
        leads: [],
        dialog: false,
        loading: false
      },
      zones: [],
      filter: {
        zones: [],
        date: {
          start: "",
          end: ""
        }
      },
      resume: {},
      items: []
    };
  },
  async mounted() {
    this.loading = true;
    await this.fetchfilters();
    await this.fetchDataByReport();
    this.zones = this.filters.zones;
  },
  methods: {
    ...mapActions({
      getReport: "reports/fetchDataByComertialReport",
      getListLeads: "reports/fetchListLeadsByDetailReport",
      fetchfilters: "leads/filter/fetchGetLeadsFilters"
    }),
    async detecPhase(contactId, phase, extraFilters = {}) {
      this.detail.loading = true;
      this.detail.dialog = true;
      await this.getListLeads({
        contactId,
        phase,
        status: this.type,
        ...this.handlerFilters(),
        ...extraFilters,
        typeReport: "comertial"
      });
      this.detail.leads = this.leadsListByContact;
      this.detail.loading = false;
    },
    async fetchDataByReport() {
      this.loading = true;
      await this.getReport(this.handlerFilters());
      this.resume = this.comertialReport.resume;
      this.items = this.comertialReport.result;
      this.loading = false;
    },
    // handler filters, valid zones
    handlerFilters() {
      const newFilter = {};
      if (this.filter.zones.length > 0) {
        newFilter.zones = this.filter.zones;
      }
      if (this.filter.date.start && this.filter.date.end) {
        newFilter.date = this.filter.date;
      }
      return newFilter;
    },
    getPercent(count, total, decimals = 2) {
      if (total === 0) {
        return "0";
      }
      return ((count / total) * 100).toFixed(decimals);
    },
    // suma algun criterio de todos los leads de un contacto
    countAllTypeByContac(contact, type) {
      const keys = {
        total: "totalByPhase",
        active: "activeInPhase",
        discarded: "discardedInPhase",
        probability: "probability",
        inmediatez: "inmediatez"
      };
      let total = 0;
      contact.data.forEach(phase => {
        total += phase[keys[type]];
      });
      return total;
    },
    // function by get value in resume by tof.allActive
    getValueAllLeadsByTOF(key, type) {
      const keys = {
        tof: "tof",
        mof: "mof",
        bof: "bof"
      };
      const types = {
        total: "allLeads",
        active: "allActive",
        discarded: "allDiscarded"
      };
      const select = keys[key];
      const discri = types[type];
      if (!key) {
        return 0;
      }
      return this.resume[select][discri];
    },
    // devuelve el valor de todos los leads por fase y tipo (headers)
    getValueAllLeadsByPhase(phase, type) {
      const keys = {
        active: "allActive",
        discarded: "allDiscarded"
      };
      const key = keys[type];
      if (!key) {
        return 0;
      }
      // valid if phase exist
      if (!this.resume.allByPhases[phase]) {
        return 0;
      }
      return this.resume.allByPhases[phase][key];
    },
    // devuelve el valor de todos los leads de un contacto por fase y tipo (items)
    getValuePhasByContact(contact, phase, key) {
      const keys = {
        total: "totalByPhase",
        active: "activeInPhase",
        discarded: "discardedInPhase",
        budgetMXN: "budgetMXN",
        budgetUSD: "budgetUSD",
        probability: "probability",
        inmediatez: "inmediatez",
        zoom: "zoomCount",
        virtualMeeting: "virtualMeetingCount"
      };
      const type = keys[key];
      if (!type) {
        return 0;
      }
      const data = contact.data.find(item => item.tracking_phase === phase);
      if (!data) {
        return 0;
      }
      return data[type];
    },
    calculatePhaseBudgetByContact(contact, phase) {
      const data = contact.data.find(item => item.tracking_phase === phase);
      if (!data) {
        return "-";
      }
      const mount = data.budgetMXN + data.budgetUSD * 20;
      if (mount === 0) {
        return "-";
      }
      return CURRENCY_FORMATER.format(mount) + " MXN";
    },
    calculateAllBudgetByPhase(currentPhase) {
      const result = this.items.reduce((acc, cur) => {
        cur.data.forEach(phase => {
          if (phase.tracking_phase === currentPhase)
            acc[phase.tracking_phase] = {
              budgetMXN: acc[phase.tracking_phase]
                ? acc[phase.tracking_phase].budgetMXN + phase.budgetMXN
                : phase.budgetMXN,
              budgetUSD: acc[phase.tracking_phase]
                ? acc[phase.tracking_phase].budgetUSD + phase.budgetUSD
                : phase.budgetUSD
            };
        });
        return acc;
      }, {});
      if (!result[currentPhase]) {
        return "-";
      }
      return CURRENCY_FORMATER.format(
        result[currentPhase]?.budgetMXN + result[currentPhase]?.budgetUSD * 20
      );
    },
    cleanFilters() {
      this.filter.zones = [];
      this.$refs.dateRange.resetDateRange();
      this.fetchDataByReport();
    },
    isIndicatorPhase(phase) {
      return ["assigned", "to-contact", "tracking"].includes(phase);
    }
  },
  computed: {
    ...mapState({
      comertialReport: state => state.reports.comertialReport,
      leadsListByContact: state => state.reports.leadListByComertialReport,
      filters: state => state.leads.filter.filters
    }),
    ...mapGetters("leads", {
      phases: "filter/getPhases"
    }),
    // devuelve el valor todos los leads por tipo(todos de la inmo)
    countAll() {
      return {
        total: this.resume.allLeads,
        active: this.resume.allActive,
        discarded: this.resume.allDiscarded
      };
    },
    initColspan() {
      return this.headers.findIndex(
        ({ value }) => value === this.customHeaders[0].value
      );
    },
    totalColspan() {
      return this.subheading.reduce((acc, { colspan }) => acc + colspan, 0);
    },
    // calcula el colspan de los subtitulos de acuerdo a las phases activas
    subheading() {
      return SUBHEADING.map(item => {
        const total = this.getValueAllLeadsByTOF(item.key, this.type);
        const percent = this.getPercent(total, this.countAll.total);
        const colspan = this.customHeaders.filter(
          ({ value }) => PHASE_MAP[value] === item.key
        ).length;
        return { ...item, colspan, total, percent };
      });
    },
    // calcula los headers de acuerdo a las phases activas
    headers() {
      const exclude = {
        brexByContact: !this.showBrex
      };
      return buildHeaders(this.customHeaders).filter(
        ({ value }) => !exclude[value]
      );
    },
    customHeaders() {
      const phases = this.phases.filter(
        phase => !["finished", "discarded", "unassigned"].includes(phase.value)
      );
      return phases.map(phase => ({
        ...phase,
        class: PHASE_CLASS[phase.value],
        cellClass: "pa-0",
        justify: "center",
        align: "center",
        sortable: false
      }));
    }
  },
  watch: {
    showDiscarded() {
      // si se activa el filtro de descartados, se desactiva el de posibilidad
      this.type = this.showDiscarded ? "discarded" : "active";
      if (this.showDiscarded) this.showBudgetPhase = false;
    },
    showBudgetPhase() {
      // si se activa el filtro de posibilidad, se desactiva el de descartados
      if (this.showBudgetPhase) this.showDiscarded = false;
    }
  }
};
</script>

<style scoped>
.filter__container {
  display: grid;
  grid-template-columns: 1fr 1fr 0.5fr 0.5fr;
  gap: 1rem;
  align-items: start;
}
.percentStyle {
  font-style: italic;
  font-size: 13px;
  font-size: smaller;
}
.v-chipActive {
  border-radius: 16px;
  font-size: 13px;
  height: 20px;
}
.logoThermometer {
  margin-top: 3px;
  width: auto;
  height: 40px;
}
.hover_tr:hover {
  background-color: #cb3838;
}
</style>
